'use client';
import {
    useState,
    createRef,
    useCallback,
    forwardRef,
    useImperativeHandle,
    Dispatch,
    SetStateAction,
    RefObject,
} from 'react';
import { Flex, Row, Col } from 'antd';
import cn from 'classnames';
import DiningDialog from '../PageDiningSingleBlock/Dialog';
import Text from '../Text';
import Heading from '../Heading';
import ImagePreview from '../ImagePreview';
import { PageOtherDiningFragment, VenueItemFragment } from '@/@types/generated/sitecore';
import { AlbumListType } from '../PageDiningSingleBlock';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import Dialog from '@/components/Dialog';
import LeaveSiteType from '@/components/Dialog/LeaveSiteType';
import WaveRightIcon from '../SVG/WaveRightIcon';
import WaveLeftIcon from '../SVG/WaveLeftIcon';
import RichText from '../RichText';
import { WATrackerClickEvn, getButtonLinkWithCID } from '@/utils/wa';
import { useRouter } from 'next/navigation';
import Button, { GeneralButtonType } from '../Button';

type LeaveDialogProps = {
    setIsLeaveModalOpen?: Dispatch<SetStateAction<boolean>>;
    isLeaveModalOpen?: boolean;
    setTargetUrl?: Dispatch<SetStateAction<string>>;
};

const OtherDiningOptionItem = ({
    item,
    leaveRef,
}: {
    item: VenueItemFragment;
    leaveRef: RefObject<LeaveDialogProps>;
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const globalStore = useGlobalStore();
    const router = useRouter();

    const onCardClick = () => {
        WATrackerClickEvn('RnE/HW_ClickBanner_Venue-listing-3Grid');
        if (item.venueLinkType?.value === 'Popup') {
            setIsModalOpen(true);
        }
        if (item.venueLinkType?.value === 'External') {
            leaveRef?.current?.setTargetUrl &&
                leaveRef.current.setTargetUrl(item.venueLink?.value || '');
            if (item.venueLink?.value) {
                if (
                    item.venueLink?.value.match(
                        /^\/(faq|dining|parties|plan-your-visit|album|season-highlight)?/,
                    )
                ) {
                    router.push(
                        getButtonLinkWithCID(
                            `/${globalStore.lang}${item.venueLink?.value}`,
                            `${globalStore.pageID}_Venue-listing-3Grid`,
                        ) || item.venueLink?.value,
                    );
                    return;
                }
                if (!item.venueLink?.value.includes('hkjc.com')) {
                    leaveRef?.current?.setIsLeaveModalOpen &&
                        leaveRef.current.setIsLeaveModalOpen(true);
                    return;
                }
                window.open(item.venueLink?.value);
            }
        }
    };

    const { venueAlbums } = item;

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const venueAlbumList = venueAlbums.map((album) => {
        if (album.template.name !== 'VenueAlbumFolder') return [{ images: [] }];
        return {
            images: album.albumList.map((imageItem, index) => {
                return {
                    id: index,
                    src: imageItem.contentImage?.src,
                    mediaType: imageItem.mediaType?.value,
                    videoSrc: imageItem.videoSrc?.value,
                    mediaCaption: imageItem.mediaCaption?.value,
                };
            }),
        };
    }) || [{ images: [] }];

    const album = (
        venueAlbumList.every(Boolean) ? venueAlbumList[0] : [{ images: [] }]
    ) as AlbumListType;

    return (
        <div>
            <Flex vertical className='h-full cursor-pointer' onClick={onCardClick}>
                <ImagePreview
                    setting={{
                        src: item?.venueHeroImage?.src,
                        className: 'rounded-t-[5px]',
                        rootClassName: 'w-full',
                    }}
                    canPreview={false}
                />
                <Flex vertical className={cn('py-6 px-5 bg-[#0D263E] flex-grow')}>
                    <Text
                        level='subtitle1'
                        text={item?.venueMealType?.value}
                        className={'opacity-50 text-base-font-color'}
                    />
                    <Heading
                        level={6}
                        text={item.venueName?.value}
                        className={cn('pt-2 text-[#EAE219]')}
                    />
                    {item?.outletLogo?.src && <ImagePreview
                        setting={{
                            src: item?.outletLogo?.src,
                            className: 'rounded-t-[5px] !h-[120px] !w-[auto]',
                            rootClassName: 'w-full py-4',
                        }}
                        canPreview={false}
                    />}
                    <Flex gap={10} align='flex-start' className='pt-4'>
                        {/* <MapIcon className={''} /> */}
                        <RichText
                            className='relative z-[8]'
                            html={item?.venueLocation?.rendered || ''}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <DiningDialog
                data={{
                    setting: {
                        open: isModalOpen,
                        onCancel: handleCancel,
                        width: 800,
                    },
                    className: 'border-[#EAE219] shadow-[0_0_10px_0_#EAE219]',
                }}
                content={item}
                album={album}
            />
        </div>
    );
};

const LeaveDialog = forwardRef<any, LeaveDialogProps>((_props: LeaveDialogProps, ref) => {
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [targetUrl, setTargetUrl] = useState('');
    useImperativeHandle(ref, () => ({
        setIsLeaveModalOpen,
        isLeaveModalOpen,
        setTargetUrl,
    }));
    return (
        <div ref={ref}>
            <Dialog
                data={{
                    setting: {
                        open: isLeaveModalOpen,
                        onCancel: () => {
                            setIsLeaveModalOpen(false);
                        },
                        width: 500,
                    },
                    className: cn('border-[#EAE219] shadow-[0_0_10px_0_#EAE219]'),
                }}
            >
                <LeaveSiteType
                    targetUrl={targetUrl}
                    onConfirm={() => {
                        window.open(targetUrl);
                        // handleLeaveCancel();
                    }}
                />
            </Dialog>
        </div>
    );
});
LeaveDialog.displayName = 'LeaveDialog';

const PageOtherDining = (props: { content: PageOtherDiningFragment }) => {
    const globalStore = useGlobalStore();
    const { content } = props;
    const leaveRef = createRef<LeaveDialogProps>();
    const diningList = content.diningOptions?.otherDiningList || [];

    const musicWaveColor = globalStore.currentTheme?.musicWaveColor;

    const TITLE_ICON = {
        titleIconRight: () => <WaveRightIcon iconColor={musicWaveColor}></WaveRightIcon>,
        titleIconLeft: () => <WaveLeftIcon iconColor={musicWaveColor}></WaveLeftIcon>,
    };

    const TitleIcon = content.titleIconOptions?.value
        ? TITLE_ICON[content.titleIconOptions?.value as keyof typeof TITLE_ICON]
        : () => null;

    return (
        <>
            <Flex vertical justify='center' align='center' className={'bg-primary-bg-color'}>
                <Flex vertical gap={8} align='center'>
                    <RichText html={content?.sectionTitle?.rendered || ''} />
                    <TitleIcon />
                </Flex>
                <Row gutter={[24, 24]} className={'py-5 xmd:py-5'}>
                    {diningList.map((item, index) => (
                        <Col key={index} xs={24} lg={8}>
                            <OtherDiningOptionItem item={item} leaveRef={leaveRef} />
                        </Col>
                    ))}
                </Row>
                {content?.buttonText?.value && (
                    <Button
                        data={{
                            buttonLink: content.buttonLink?.value,
                            className: 'mt-6 mb-24',
                        }}
                    >
                        {GeneralButtonType({
                            label: content.buttonText?.value,
                            icon: 'arrowRightBK',
                        })}
                    </Button>
                )}
            </Flex>
            <LeaveDialog ref={leaveRef} />
        </>
    );
};

export default PageOtherDining;
